import {Components, CssVarsTheme, Theme} from "@mui/material";
import type {} from "@mui/x-data-grid/themeAugmentation";
import {AppTheme} from "@theme";

const getBorderColor = (theme: Theme) => (theme as AppTheme).colors.border;

const muiComponentCustomization: Components<
  Omit<Theme, "components" | "palette"> & CssVarsTheme
> = {
  MuiToolbar: {
    styleOverrides: {
      root: {
        height: 64,
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: () => ({
        "& .MuiCardHeader-root .MuiTypography-root": {
          lineHeight: "18px",
          fontWeight: 600,
          fontSize: "1.3rem",
        },
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        fontSize: "1rem",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        fontSize: "1rem",
        textTransform: "capitalize",
      },
      contained: {
        borderRadius: 12,
        boxShadow: "none", // MD3 riduce le ombre sui bottoni filled
        "&:hover": {
          boxShadow: "none",
        },
      },
      outlined: ({theme}) => ({
        color: theme.palette.text.primary,
        borderRadius: 12,
        borderColor: getBorderColor(theme),
        "&:hover": {
          backgroundColor: "rgba(103, 80, 164, 0.1)", // Leggero fill sul bordo
        },
      }),
      text: ({theme}) => ({
        borderRadius: 12,
        color: theme.palette.action.active,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.05)", // Effetto hover morbido
        },
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 12,
          "& .MuiInputBase-input": {
            padding: "8px 16px",
          },
          "& .MuiInputBase-input.MuiSelect-select": {
            padding: "8px 32px 8px 16px",
          },
          "& .MuiInputBase-inputMultiline": {
            padding: "0",
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        boxShadow: "none",
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        "& .MuiDialog-paper": {
          borderRadius: 16,
        },
        "& .MuiDialog-paperFullScreen": {
          borderRadius: 0,
        },
        "& .MuiDialogActions-root": {
          padding: 24,
          gap: 8,
        },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({theme}) => ({
        "& a": {
          color: theme.palette.secondary.main,
          textDecoration: "none",
        },
      }),
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({theme}) => ({
        backgroundColor: (theme as unknown as AppTheme).colors
          .secondaryContainer,
      }),
    },
  },
  MuiBottomNavigation: {
    styleOverrides: {
      root: {
        backgroundColor: "transparent",
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({theme}) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({theme}) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: "8px 12px !important",
        height: 48,
        "& .MuiListItemIcon-root": {
          minWidth: 28,
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({theme}) => ({
        border: "none",
        backgroundColor: theme.palette.background.paper,
        "& .MuiTablePagination-actions": {
          marginRight: 8,
        },
        "& .MuiDataGrid-footerContainer": {
          minHeight: 64,
        },
      }),
    },
  },
};

export {muiComponentCustomization};
